// apps/mobile/src/app/services/chatThreadService.ts
import apiClient from './apiClient';
import type { ApiRequestOptions } from './apiClient';

export interface ChatThreadSummary {
  id: string;
  title: string;
  context_type: 'general' | 'paper' | 'session' | 'speaker' | 'exhibitor';
  context_id: string | null;
  context_date: string | null;
  event_id: string | null;
  created_at: string;
  updated_at: string;
  last_message: string | null;
  summary: string | null;
}

export interface ChatMessageRecord {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  receipt_id: string | null;
  created_at: string;
}

export interface ChatAppendResult {
  appended: number;
  summary: string | null;
}

export interface CreateThreadParams {
  event_id?: string;
  title: string;
  context_type: 'general' | 'paper' | 'session' | 'speaker' | 'exhibitor';
  context_id?: string;
}

export interface FindOrCreateParams {
  event_id?: string;
  context_type: 'general' | 'paper' | 'session' | 'speaker' | 'exhibitor';
  context_id?: string;
  title: string;
}

const chatThreadService = {
  async list(
    eventId?: string,
    options: ApiRequestOptions = {},
  ): Promise<ChatThreadSummary[]> {
    const qs = eventId ? `?event_id=${eventId}` : '';
    return apiClient.get<ChatThreadSummary[]>(`/chat/threads${qs}`, options);
  },

  async create(params: CreateThreadParams): Promise<ChatThreadSummary> {
    return apiClient.post<ChatThreadSummary>('/chat/threads', params);
  },

  async findOrCreate(
    params: FindOrCreateParams,
    options: ApiRequestOptions = {},
  ): Promise<ChatThreadSummary> {
    return apiClient.post<ChatThreadSummary>(
      '/chat/threads/find-or-create',
      params,
      options,
    );
  },

  async rename(
    threadId: string,
    title: string,
    options: ApiRequestOptions = {},
  ): Promise<ChatThreadSummary> {
    return apiClient.patch<ChatThreadSummary>(
      `/chat/threads/${threadId}`,
      { title },
      options,
    );
  },

  async delete(
    threadId: string,
    options: ApiRequestOptions = {},
  ): Promise<void> {
    await apiClient.delete(`/chat/threads/${threadId}`, options);
  },

  async getMessages(
    threadId: string,
    options: ApiRequestOptions = {},
    forAI = true,
  ): Promise<ChatMessageRecord[]> {
    return apiClient.get<ChatMessageRecord[]>(
      `/chat/threads/${threadId}/messages${forAI ? '?for_ai=true' : ''}`,
      options,
    );
  },

  async appendMessages(
    threadId: string,
    messages: { role: 'user' | 'assistant'; content: string; receipt_id?: string }[],
    options: ApiRequestOptions = {},
  ): Promise<ChatAppendResult> {
    return apiClient.post<ChatAppendResult>(
      `/chat/threads/${threadId}/messages`,
      { messages },
      options,
    );
  },
};

export default chatThreadService;
